import { useParams } from "react-router-dom";

import { LoadScreen, PageBackButton } from "@/core/components/shared";
import { Button } from "@/core/components/ui/button";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { usePayrollBatch } from "@/features/payroll/api/use-payroll-batch";
import {
  PayrollBatchActions,
  PayrollBatchSummaryCard,
  PayrollLinesSection,
} from "@/features/payroll/components";

/**
 * Détail d'une paie (`GET /PayrollBatches/{id}`, guide v4.1 §6.11) — carte de
 * synthèse, actions orchestrées (simulate / launch / cancel / refresh +
 * téléchargements de relevés), tableau des lignes avec édition inline et
 * téléchargement du relevé individuel pour les lignes `PAID`. Route cible :
 * `/app/payroll/:batchId`.
 */
export function PayrollBatchDetailPage() {
  const { t } = useTypedTranslation();
  const params = useParams();
  const batchId = params.batchId ?? "";
  const { data: batch, isLoading, error, refetch } = usePayrollBatch(batchId);

  const backButton = (
    <PageBackButton to="/app/payroll" label={t("common.back")} />
  );

  if (isLoading) {
    return (
      <section className="flex flex-col gap-4">
        {backButton}
        <div className="flex h-64 items-center justify-center">
          <LoadScreen />
        </div>
      </section>
    );
  }

  if (error || !batch) {
    return (
      <section className="flex flex-col gap-4">
        {backButton}
        <div className="flex flex-col items-center gap-4 py-24">
          <p className="font-semibold text-destructive">
            {error?.message ?? t("common.error")}
          </p>
          <Button variant="secondary" size="sm" onClick={() => void refetch()}>
            {t("common.retry")}
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center gap-2">{backButton}</div>
      <PayrollBatchSummaryCard batch={batch} />
      <PayrollBatchActions batch={batch} />
      <PayrollLinesSection batch={batch} />
    </section>
  );
}
